// src/core/mvcs/mediators/AbstractMediator.ts
import type { Application } from 'pixi.js';
import type { SignalBus } from '../../signal/SignalBus';
import type { ModelMap } from '../model/ModelMap';
import { AbstractView } from './AbstractView';
import type { MediatorMap } from './MediatorMap';

/**
 * Base class for all Mediators in the MVCS architecture.
 * Provides a standardized lifecycle for view management, including:
 * 1. Automatic dependency injection (App, SignalBus, Models).
 * 2. Standardized responsive layout handling.
 * 3. Lifecycle hooks for registration and stage entry.
 */
export abstract class AbstractMediator<T extends AbstractView> {
    public static readonly BACK_CLICK_EVENT = 'backClickEvent';

    protected view!: T;
    protected app!: Application;
    protected signalBus!: SignalBus;
    protected modelMap!: ModelMap;
    protected mediatorMap!: MediatorMap;

    /**
     * Constructor for the AbstractMediator class.
     * Sets the view instance that the mediator will manage.
     * 
     * @param view - The view instance to be managed.
     */
    constructor(view: T) {
        this.view = view;
    }

    /**
     * Called after the mediator is registered.
     * Sets up the event listener for the back button click event and
     * triggers the setup of the responsive layout.
     * Also sets up a one-time event listener for the view added to root event.
     */
    public onRegister(): void {
        console.debug(`[${this.constructor.name}] Mediator registered.`);
        this.setupResponsiveLayout();
        this.initListener();
    }

    /**
     * Initializes the one-time event listener for the view added to root event.
     * When the event is triggered, it calls the onViewAddedToRoot method to
     * trigger a layout update and log a debug message to indicate that the
     * view has been added to the stage.
     */
    protected initListener(): void {
        this.view.once(AbstractView.VIEW_ADDED_TO_ROOT_EVENT, () => this.onViewAddedToRoot());
    }

    /**
     * Called when the mediator is no longer needed.
     * Removes all event listeners and cleans up the mediator for garbage collection.
     * Offs the 'resize' event listener on the PIXI renderer and the
     * AbstractMediator.BACK_CLICK_EVENT event listener on the view component.
     */
    public onRemove(): void {
        console.debug(`[${this.constructor.name}] Mediator removed.`);
        this.cleanUp();
    }

    protected cleanUp(): void {
        if (this.app?.renderer?.off) {
            this.app.renderer.off('resize', this.onResize);
        }
    }

    /**
     * Sets up the responsive layout for the view by triggering a layout update
     * via requestAnimationFrame and adding a listener for the 'resize' event on the
     * PIXI renderer. This ensures that the view is always laid out according to the
     * current screen size and resolution.
     */
    // Inside AbstractMediator.ts
    protected setupResponsiveLayout(): void {
        // If there's no renderer (like in a Unit Test), just skip it.
        if (!this.app?.renderer?.on) return;
        this.app.renderer.on('resize', this.onResize);
    }

    /**
     * Event listener for the 'resize' event on the PIXI renderer.
     * Triggers a layout update when the event is triggered.
     */
    private readonly onResize = (): void => {
        this.triggerLayout();
    };

    /**
     * Called when the view is added to the stage.
     * Triggers a layout update and logs a debug message to indicate that the view
     * has been added to the stage.
     */
    protected onViewAddedToRoot(): void {
        this.view.onAddedToRoot();
        this.triggerLayout();
    }

    /**
     * Triggers a layout update for the view if the application's screen is available.
     * The layout update is skipped if the screen dimensions are invalid (i.e., width or height is less than or equal to 0),
     * or if the layout validation fails.
     * Logs debug messages to indicate skipped layout updates due to invalid screen dimensions or failed layout validation.
     */
    protected triggerLayout(): void {
        if (!this.app?.screen) {
            console.debug("[AbstractMediator] Layout skipped: App/Screen not available.");
            return;
        }

        const { width, height } = this.app.screen;

        if (width <= 0 || height <= 0) {
            console.warn('[AbstractMediator] Skipping layout update due to collapsed renderer');
            return;
        }

        const isLayoutValid = this.isLayoutValid(width, height);
        if (!isLayoutValid) {
            console.warn('[AbstractMediator] Layout validation failed.');
            return;
        }

        this.view.layout(width, height);
    }

    /**
     * Architectural Guard: Validation logic for the layout lifecycle.
     * Prevents UI "flickering" or "jumping" by filtering out anomalous 
     * browser dimension reports during rapid resizing or orientation changes.
     * 
     * @param width - The target layout width.
     * @param height - The target layout height.
     * @returns True if dimensions are within a realistic range.
     */
    protected isLayoutValid(width: number, height: number): boolean {
        return width > 0 && height > 0;
    }


    /**
     * Sets the Application instance used for rendering and event dispatching.
     * 
     * @param app - The Application instance to be used.
     */
    public setApp(app: Application): void {
        this.app = app;
    }

    /**
     * Sets the SignalBus instance used for event dispatching.
     * 
     * @param bus - The SignalBus instance to be used.
     */
    public setSignalBus(bus: SignalBus): void {
        this.signalBus = bus;
    }

    /**
     * Sets the ModelMap instance used for accessing Models.
     * @param map - The ModelMap instance to be used.
     */
    public setModelMap(map: ModelMap): void {
        this.modelMap = map;
    }

    /**
     * Sets the MediatorMap instance used for registering and unregistering Mediators.
     * @param map - The MediatorMap instance to be used.
     */
    public setMediatorMap(map: MediatorMap): void {
        this.mediatorMap = map;
    }

    /**
     * Returns the view component. Can be cast to the specific view type.
     */
    protected get viewComponent(): T { return this.view; }
}